import { useCallback } from 'react'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { InsuranceFormPageState } from '@typesApp/InsuranceForm'
import { ViewName } from '@typesApp/ViewNavigation'
import { ROUTES } from '@constants/index'
import { useAppControl } from '@hooks/useAppControl'
import { useFetchClaimRecord } from '@hooks/useFetchClaimRecord'
import { isClaimAcceptedStatus, isRestrictedStatus } from '@utils/claimUtil'
import { drawerEventEmitter } from '@utils/drawerEventEmitter'
import { clearLocalFormState, getLocalFormState } from '@utils/localFormState'
import { getResumeToView } from '@utils/resumeToView'
import { isClaimRecordInProgress } from '../helpers/claimRecordInProgress'
import { useFallbackHandler } from './useFallbackHandler'

type ResumeParams = {
  id: string
  isNewClaimRecord: boolean
}

type UseResumeReturn = {
  resumeFlow: (params: ResumeParams) => Promise<void>
}

const ViewNameToRouteMap: Record<ViewName, string> = {
  start: ROUTES.START,
  'insurance-information': ROUTES.INSURANCE_INFORMATION,
  insurers: ROUTES.ELIGIBLE_INSURERS,
  intro: ROUTES.BASE,
  quote: ROUTES.QUOTE,
  review: ROUTES.REVIEW_CLAIM,
  'select-prescription': ROUTES.SELECT_PRESCRIPTION,
  'select-product': ROUTES.SELECT_PRODUCT,
  validate: ROUTES.VALIDATE_PRESCRIPTION,
} as const

export const useResumeFlow = (): UseResumeReturn => {
  const { closeApp } = useAppControl()
  const { fetchClaimRecord } = useFetchClaimRecord()
  const { showErrorPage, handleRestricted } = useFallbackHandler()

  const resumeFlow = useCallback(
    async ({ id, isNewClaimRecord }: ResumeParams) => {
      const resumeWithFallback = (resumeToView?: ViewName) => {
        const target = resumeToView ? ViewNameToRouteMap[resumeToView] : undefined
        if (!target) {
          showErrorPage()
          return
        }
        drawerEventEmitter.openDrawer({ target })
      }

      const resumeToInsuranceForm = () => {
        // to handle multi step insurance form
        const formState = getLocalFormState()
        const state: InsuranceFormPageState = { formIndex: formState?.formIndex }
        drawerEventEmitter.openDrawer({ target: ROUTES.INSURANCE_INFORMATION, navigateState: state })
      }

      const resumeInProgress = (claimRecord: ClaimRecord) => {
        const resumeToView = getResumeToView(claimRecord)

        // clear local form state unless resuming to insurance form

        if (resumeToView === 'insurance-information') {
          resumeToInsuranceForm()
        } else {
          clearLocalFormState()
          resumeWithFallback(resumeToView)
        }
      }

      // resume to view based on claim record status
      try {
        if (isNewClaimRecord) {
          clearLocalFormState()
        }

        const claimRecord = await fetchClaimRecord(id)

        // resume to view configured in pageflow
        if (isClaimRecordInProgress(claimRecord)) {
          resumeInProgress(claimRecord)
        } else {
          if (isRestrictedStatus(claimRecord?.status)) {
            handleRestricted()
          } else if (isClaimAcceptedStatus(claimRecord?.status)) {
            closeApp()
          } else {
            showErrorPage()
          }
        }
      } catch {
        showErrorPage()
      }
    },
    [fetchClaimRecord, showErrorPage, closeApp, handleRestricted]
  )

  return {
    resumeFlow,
  }
}
